import React from 'react';

import repositoryCss from './repository.css';
import { useAuthStore, usePackagesStore, useRepositoriesStore } from '../../../../store';
import {
    IManualFileUploadProps,
    ManualFileUpload,
    DangerAlert,
    SuccessAlert,
    BasicForm,
    SelectFormItem,
    IconTextButton,
    TextFieldItem,
    useForm,
} from 'ui-lib';
import { usePackageContext } from './package-tab';
import { ICreatePackageRequestProps } from '../../../../dto/packages-dto';
import { useUploadFilesCheckingStates } from './package-utils';
import { RepositoryDto } from 'dto/repository.dto';

interface IFileUploadResult {
    successFiles?: any[];
    errors?: any[];
}

export const UploadPackagesWrapper = () => {
    const context = usePackageContext();
    const [form] = useForm();
    const packagesStore = usePackagesStore();
    const { currentUser, assureApiAccount } = useAuthStore();
    const repositoriesStore = useRepositoriesStore();
    const [errors, setErrors] = React.useState<string[]>([]);
    const [successFiles, setSuccessFiles] = React.useState<string[]>([]);
    const [repositoryType, setRepositoryType] = React.useState(currentUser.accountType == 'DPU' ? 'ALM' : currentUser.accountType);
    const [repositoryUuid, setRepositoryUuid] = React.useState('');
    const [repositoriesSource, setRepositoriesSource] = React.useState<RepositoryDto[]>([]);

    const { validateFiles } = useUploadFilesCheckingStates();

    React.useEffect(() => {
        (async () => {
            const repositories = await repositoriesStore.getRepositories(currentUser.accountId, currentUser.accountUuid);
            setRepositoriesSource(repositories);
        })();
    }, []);

    React.useEffect(() => {
        if (repositoryUuid){
            repositoriesSource.find((repo) => {
                if (repo.uuid === repositoryUuid) {
                    setRepositoryType(repo.type);
                    form.setFieldsValue({
                        repositoryType: repo.type,
                    });
                    return true;
                }
            });
        }
    }, [repositoryUuid]);

    const draggerProps: IManualFileUploadProps<IFileUploadResult> = {
        limitFileSize: __LIMIT_PACKAGE_FILE_SIZE,
        onUploadFiles: async (params: { files: any[] }): Promise<IFileUploadResult> => {
            const options: ICreatePackageRequestProps = {
                ...params,
                type: repositoryType,
                repositoryUuid: repositoryUuid,
            };
            const result = await packagesStore.createPackages(assureApiAccount.accountId, options);
            return {
                successFiles: result.packages.map((item) => item.fileName),
                errors: result.errors,
            };
        },
        onSuccessOrError: (response: IFileUploadResult) => {
            setSuccessFiles(response?.successFiles || []);
            setErrors(response?.errors || []);
        },
        beforeUploadChecking: (currentFiles: File[], newFiles: File[]): File[] => {
            const checkFileResult = validateFiles(currentFiles, newFiles);
            return checkFileResult.validFiles;
        },
        hideSelectButton: true,
    };

    return (
        <React.Fragment>
            {successFiles.length != 0 && (
                <SuccessAlert
                    className={repositoryCss.uploadAlert}
                    closable={true}
                    message='Imported files'
                    description={
                        <React.Fragment>
                            <p key='success-files'>Success files: {successFiles.join(', ')}</p>
                        </React.Fragment>
                    }
                />
            )}
            {errors.length != 0 && (
                <DangerAlert
                    className={repositoryCss.uploadAlert}
                    closable={true}
                    message='Errors'
                    description={
                        <React.Fragment>
                            {errors.map((error, index) => (
                                <p key={`error-${index}`}>{error}</p>
                            ))}
                        </React.Fragment>
                    }
                />
            )}
            <div className={repositoryCss.upload}>
                <h2>Upload Packages</h2>
                <div>
                    <BasicForm
                        form={form}
                        cardPros={{ bordered: false }}
                        items={[
                            <SelectFormItem
                                code='repositoryName'
                                label='Repository'
                                colon={true}
                                isRequired={true}
                                labelAlign='right'
                                dataSource={repositoriesSource.map(repos => ({ value: repos.uuid, label: repos.label }))}
                                selectedValue={repositoryUuid}
                                onChange={(value) => setRepositoryUuid(value)}
                            />,
                            <TextFieldItem
                                key='repositoryType'
                                code='repositoryType'
                                label='Repository Type'
                                colon={true}
                                labelAlign='right'
                                value={repositoryType}
                                disabled={true}
                            />,
                        ]}
                    />
                </div>
                <ManualFileUpload {...draggerProps} />
                <IconTextButton type='primary' label='Back to Software Repositories and Packages' onClick={context.toPackagesTable} />
            </div>
        </React.Fragment>
    );
};
